<script setup>
import MagnifyingGlassVue from "@/Components/MagnifyingGlass.vue";
import EditIcons from "@/Components/Icons/EditIcons.vue";
import DeleteIcons from "@/Components/Icons/DeleteIcons.vue";
import InputField from "@/Components/InputField.vue";
import PrimaryBtn from "@/Components/PrimaryBtn.vue";
import InputError from "@/Components/InputError.vue";
import Breadcrumbs from "@/Components/Breadcrumbs.vue";
import Pagination from "@/Components/Pagination.vue";
import Header from "@/Components/Header.vue";
import Container from "@/Layouts/Container.vue";
import { router, useForm, usePage } from "@inertiajs/vue3";
import { computed, ref, watch, inject } from "vue";
import Swal from "sweetalert2";
import vSelect from "vue-select";
const route = inject('route');

const props = defineProps({
  permissions: {
    type: Object,
    required: true,
  },
});

let search = ref(usePage().props.search || ""),
  pageNumber = ref(1);

// Computed property for row number calculation with fallbacks
const getRowNumber = computed(() => {
  return (index) => {
    const currentPage = props.permissions?.current_page || 1;
    const perPage = props.permissions?.per_page || 10;
    return (currentPage - 1) * perPage + index + 1;
  };
});

// Format date function
const formatDate = (dateString) => {
  if (!dateString) return "-";
  const date = new Date(dateString);
  return date.toLocaleDateString("id-ID", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

let permissionsUrl = computed(() => {
  let url = new URL(route("permissions.index"));
  url.searchParams.append("page", pageNumber.value);
  if (search.value) {
    url.searchParams.append("search", search.value);
  }

  return url;
});

const updatedPageNumber = (link) => {
  if (link && link.url) {
    try {
      const urlParams = new URLSearchParams(link.url.split("?")[1] || "");
      const page = urlParams.get("page");
      if (page) {
        pageNumber.value = parseInt(page);
      }
    } catch (error) {
      console.error("Error parsing pagination URL:", error);
    }
  }
};

watch(
  () => permissionsUrl.value,
  (updatedPermissionsUrl) => {
    router.visit(updatedPermissionsUrl, {
      preserveScroll: true,
      preserveState: true,
      replace: true,
    });
  }
);

watch(
  () => search.value,
  (value) => {
    if (value) {
      pageNumber.value = 1;
    }
  }
);

const deleteForm = useForm({});

const deletePermission = (permissionId) => {
  Swal.fire({
    title: "Apakah Anda yakin?",
    text: "Permission ini akan dihapus secara permanen!",
    icon: "warning",
    showCancelButton: true,
    confirmButtonColor: "#dc2626",
    cancelButtonColor: "#6b7280",
    confirmButtonText: "Ya, hapus!",
    cancelButtonText: "Batal",
  }).then((result) => {
    if (result.isConfirmed) {
      deleteForm.delete(route("permissions.destroy", permissionId), {
        onSuccess: () => {},
        onError: () => {
          Swal.fire({
            title: "Gagal!",
            text: "Terjadi kesalahan saat menghapus permission.",
            icon: "error",
            confirmButtonText: "OK",
          });
        },
      });
    }
  });
};

const editPermission = (permission) => {
  if (!permission) {
    console.error("Permission is undefined");
    return;
  }

  form.id = permission.id;
  form.title = permission.title;
};

const form = useForm({
  id: null,
  title: "",
});

const submit = () => {
  if (form.id) {
    form.put(route("permissions.update", form.id), {
      onSuccess: () => {
        form.reset();
        Swal.fire({
          title: "Berhasil!",
          text: "Data berhasil diperbarui.",
          icon: "success",
          confirmButtonText: "OK",
        });
      },
      onError: () => {
        Swal.fire({
          title: "Gagal!",
          text: "Terjadi kesalahan saat memperbarui data.",
          icon: "error",
          confirmButtonText: "OK",
        });
      },
    });
  } else {
    form.post(route("permissions.store"), {
      onSuccess: () => {
        form.reset();
        Swal.fire({
          title: "Berhasil!",
          text: "Data berhasil disimpan.",
          icon: "success",
          confirmButtonText: "OK",
        });
      },
      onError: () => {
        Swal.fire({
          title: "Gagal!",
          text: "Terjadi kesalahan saat menyimpan data.",
          icon: "error",
          confirmButtonText: "OK",
        });
      },
    });
  }
};
</script>

<template>
  <Head title="Permission" />
  <Container>
    <template #header>
      <Header :menu="$page.props.main_menu" v-if="$page.props.auth.user" />
    </template>
    <template #default>
      <div>
        <Breadcrumbs
          :breadcrumbs="[
            { name: 'Dashboard', href: route('dashboard') },
            { name: 'Permission', href: route('permissions.index') },
          ]"
        />

        <div class="grid grid-cols-1 gap-6 lg:grid-cols-3 lg:gap-8">
          <div class="order-2 col-span-1 space-y-6 lg:order-1">
            <div class="bg-white border border-gray-200 shadow-lg rounded-xl">
              <div
                class="px-4 py-3 border-b border-gray-200 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-t-xl sm:px-6 sm:py-4"
              >
                <h2
                  class="flex items-center text-lg font-bold text-white sm:text-xl"
                >
                  <i class="mr-2 fa-solid fa-shield-halved sm:mr-3"></i>
                  {{ form.id ? "Edit Permission" : "Tambah Permission" }}
                </h2>
              </div>
              <div class="p-4 sm:p-6">
                <form class="space-y-4" @submit.prevent="submit">
                  <!-- Input Fields -->
                  <InputField
                    label="Nama Permission"
                    icon="pen"
                    v-model="form.title"
                  />
                  <InputError :message="form.errors.title" class="mt-2" />

                  <!-- Submit Button -->
                  <div class="pt-4">
                    <PrimaryBtn
                      :disabled="form.processing"
                      class="w-full px-4 py-2.5 text-sm font-semibold text-white bg-indigo-600 border border-transparent rounded-lg shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                    >
                      <i class="mr-2 fa-solid fa-floppy-disk"></i>
                      {{ form.processing ? "Menyimpan..." : "Simpan" }}
                    </PrimaryBtn>
                  </div>
                </form>
              </div>
            </div>
          </div>
          <div class="order-1 col-span-1 lg:col-span-2 lg:order-2">
            <!-- Header Section -->
            <div class="mb-4 sm:mb-6">
              <div
                class="flex flex-col sm:flex-row sm:items-center sm:justify-between"
              >
                <div>
                  <h3 class="text-lg font-semibold text-gray-900">
                    Daftar Permission
                  </h3>
                  <p class="mt-1 text-sm text-gray-600">
                    Kelola permission sistem dan akses kontrol
                  </p>
                </div>
                <div class="mt-2 text-sm text-gray-500 sm:mt-0">
                  Total: {{ permissions.total }} permission
                </div>
              </div>
            </div>

            <!-- Search Input -->
            <div class="relative mb-4">
              <div
                class="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none"
              >
                <MagnifyingGlassVue class="w-4 h-4 text-gray-400" />
              </div>
              <input
                v-model="search"
                type="text"
                autocomplete="off"
                placeholder="Search data..."
                id="search"
                class="block w-full px-3 py-2.5 pl-10 text-sm text-gray-900 bg-white border border-gray-300 rounded-lg shadow-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 hover:border-gray-400 transition-colors duration-200"
              />
            </div>

            <!-- Table -->
            <div
              class="overflow-hidden bg-white shadow ring-1 ring-black ring-opacity-5 md:rounded-lg"
            >
              <div class="overflow-x-auto">
                <table class="min-w-full divide-y divide-gray-300">
                  <thead class="bg-gray-50">
                    <tr>
                      <th
                        scope="col"
                        class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6"
                      >
                        #
                      </th>
                      <th
                        scope="col"
                        class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6 min-w-[150px]"
                      >
                        Permission
                      </th>
                      <th
                        scope="col"
                        class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 hidden md:table-cell min-w-[120px]"
                      >
                        Created
                      </th>
                      <th
                        scope="col"
                        class="relative py-3.5 pl-3 pr-4 sm:pr-6 min-w-[100px]"
                      >
                        <span class="sr-only">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody class="bg-white divide-y divide-gray-200">
                    <!-- Table Rows -->
                    <tr
                      v-for="(permission, index) in permissions.data"
                      :key="permission.id"
                      class="transition-colors duration-150 hover:bg-gray-50"
                    >
                      <td
                        class="py-4 pl-4 pr-3 text-sm font-medium text-gray-900 whitespace-nowrap sm:pl-6"
                      >
                        {{ getRowNumber(index) }}
                      </td>
                      <td class="px-3 py-4 text-sm">
                        <div class="flex items-center">
                          <span class="mr-2 text-indigo-600">🛡️</span>
                          <span class="font-medium text-gray-900">
                            {{ permission.title }}
                          </span>
                        </div>
                      </td>
                      <td
                        class="hidden px-3 py-4 text-sm text-gray-500 whitespace-nowrap md:table-cell"
                      >
                        {{ formatDate(permission.created_at) }}
                      </td>
                      <td
                        class="relative py-4 pl-3 pr-4 text-sm font-medium text-right sm:pr-6"
                      >
                        <div
                          class="flex items-center justify-end space-x-1 sm:space-x-2"
                        >
                          <!-- Action Buttons (Edit, Delete) -->
                          <button
                            @click="editPermission(permission)"
                            class="inline-flex items-center px-2 py-1 text-xs font-medium text-indigo-600 bg-indigo-100 rounded-md hover:bg-indigo-200 hover:text-indigo-700 transition-colors duration-150 sm:px-2.5 sm:py-1.5"
                            title="Edit"
                          >
                            <EditIcons class="w-3 h-3" />
                            <span class="hidden ml-1 sm:inline">Edit</span>
                          </button>
                          <button
                            @click="deletePermission(permission.id)"
                            class="inline-flex items-center px-2 py-1 text-xs font-medium text-red-600 bg-red-100 rounded-md hover:bg-red-200 hover:text-red-700 transition-colors duration-150 sm:px-2.5 sm:py-1.5"
                            title="Delete"
                          >
                            <DeleteIcons class="w-3 h-3" />
                            <span class="hidden ml-1 sm:inline">Delete</span>
                          </button>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <!-- Pagination -->
            <Pagination
              :data="permissions"
              :updatedPageNumber="updatedPageNumber"
            />
          </div>
        </div>
      </div>
    </template>
  </Container>
</template>
